// Groups the raw SocialNotification `type` values (types/index.ts) into the
// user-facing categories shown in Settings → Notifications. The in-app toggles
// filter the notification bell by category.

import type { SocialNotification } from '../types';

export type NotificationCategory = 'social' | 'messages' | 'connections' | 'invitations' | 'updates';

// 'release' is What's new (Phase 2) — not a table type, but mutable like one.
type NType = SocialNotification['type'] | 'release';

export const NOTIFICATION_CATEGORIES: {
  id: NotificationCategory;
  label: string;
  description: string;
  types: NType[];
}[] = [
  {
    id: 'social',
    label: 'Social activity',
    description: 'Likes, comments, replies, reposts, mentions and follows.',
    types: ['follow', 'like', 'comment', 'reply', 'repost', 'mention', 'copy'],
  },
  {
    id: 'messages',
    label: 'Messages',
    description: 'Direct messages.',
    types: ['message'],
  },
  {
    id: 'connections',
    label: 'Connections & pacts',
    description: 'Connection requests, accepts, and pact activity.',
    types: ['network_request', 'network_accepted', 'pact_request', 'pact_accepted', 'pact_nudge'],
  },
  {
    id: 'invitations',
    label: 'Invitations & requests',
    description: 'Project invites, task assignments, comment mentions and community request updates.',
    types: ['comment_mention', 'task_assigned', 'project_invite', 'request_claimed', 'request_fulfilled'],
  },
  {
    id: 'updates',
    label: 'App updates',
    description: 'What changed in each new version of Metanoia.',
    types: ['release'],
  },
];

const TYPE_TO_CATEGORY: Record<string, NotificationCategory> = Object.fromEntries(
  NOTIFICATION_CATEGORIES.flatMap((c) => c.types.map((t) => [t, c.id])),
);

export function categoryForType(type: string): NotificationCategory | null {
  return TYPE_TO_CATEGORY[type] ?? null;
}
